import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AssignablePlannerOut,
  useWebApiV1SetOrderAssignmentMutation,
} from "@providers/hop-ord-server/api";
import { AssigneeOption } from "./types";

interface UseAssignedToSelectProps {
  orderId: number;
  assignedDosimetrist?: AssigneeOption | null;
  assignablePlanners?: AssignablePlannerOut[];
}

export const useAssignedToSelect = ({
  orderId,
  assignedDosimetrist,
  assignablePlanners = [],
}: UseAssignedToSelectProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [setAssignment] = useWebApiV1SetOrderAssignmentMutation();

  // Optimistic local state
  const [optimisticAssignee, setOptimisticAssignee] = useState<
    AssigneeOption | null | undefined
  >(assignedDosimetrist);

  // Sync optimistic state with prop changes (e.g., from successful mutation or refetch)
  useEffect(() => {
    setOptimisticAssignee(assignedDosimetrist);
  }, [assignedDosimetrist]);

  const assignedTo: AssigneeOption = optimisticAssignee ?? {
    id: -1,
    fullName: "Unassigned",
  };

  const isUnassigned = assignedTo.id === -1;

  const options: AssigneeOption[] = useMemo(
    () =>
      assignablePlanners.map(
        (p: AssignablePlannerOut): AssigneeOption => ({
          id: p.id,
          fullName: p.fullName,
          workloadCount: p.workloadCount,
        }),
      ),
    [assignablePlanners],
  );

  const handleOpen = useCallback((event?: React.MouseEvent) => {
    event?.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleChange = useCallback(
    async (_: React.SyntheticEvent, value: AssigneeOption | null) => {
      if (!value) return;

      // Store the previous value for rollback on error
      const previousAssignee = optimisticAssignee;

      // Optimistically update the UI immediately
      setOptimisticAssignee(value.id === -1 ? null : value);
      setIsEditing(false);

      try {
        // Make the API call
        await setAssignment({
          orderId,
          orderAssignmentIn: {
            assignedToId: value.id === -1 ? null : value.id,
          },
        }).unwrap();
      } catch (error) {
        // Revert to previous value on error
        setOptimisticAssignee(previousAssignee);
        console.error("Failed to update assignment:", error);
      }
    },
    [orderId, setAssignment, optimisticAssignee],
  );

  return {
    isEditing,
    assignedTo,
    isUnassigned,
    options,
    handleOpen,
    handleClose,
    handleChange,
  };
};
